/**
 * Client ของ Customer Master — พูดกับ `/v1/customers` ตาม payload contract ที่
 * freeze ไว้ใน `docs/API_CONTRACT.md` (D-089)
 *
 * หลักการเดียวกับ `lib/suppliers/client.ts` ซึ่งเป็นฝาแฝดของไฟล์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีลูกค้า"** — รายการว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่า "ยังไม่มีใครลงทะเบียน
 *   ลูกค้าไว้" แล้วสร้างรหัสที่มีเจ้าของอยู่แล้วซ้ำเข้าไป
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *   (D-049 อนุญาตเฉพาะ session token)
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * เส้นทางที่ contract freeze ไว้มีสี่เส้น และไฟล์นี้ implement **ครบทั้งสี่**
 * `fetchCustomer` (`GET /v1/customers/{customer_id}`) เป็นเส้นที่หน้ารายละเอียด
 * ลูกค้าเรียก (`CustomerDetailView`, D-123 ข้อ 6) — ที่อยู่และผู้ติดต่อของ D-121 มา
 * ทางนั้น ไม่ใช่จากแถวในตาราง
 *
 * `fetchAllCustomers` ด้านล่าง **ไม่ใช่เส้นที่ห้า** มันคือ `fetchCustomers` ที่ถูก
 * เรียกซ้ำตาม `next_cursor` จนครบ (D-123 ข้อ 1) — หน้าจอรายการเรียงและแบ่งหน้าฝั่ง
 * เบราว์เซอร์ จึงต้องถือทั้งชุด ไม่ใช่หน้าแรกที่เซิร์ฟเวอร์เลือกมาให้
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateCustomerBody,
  Customer,
  CustomerContact,
  CustomerListPage,
  UpdateCustomerBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const CUSTOMERS_PATH = '/v1/customers';

/**
 * เส้นทางของลูกค้ารายเดียว — `customer_id` เป็น **UUID surrogate** (D-039)
 * ไม่ใช่ `customer_code` ซึ่งเป็น business key
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทาง
 * มาถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือ
 * วิธีที่คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้
 */
export function customerPath(customerId: string): string {
  return `${CUSTOMERS_PATH}/${encodeURIComponent(customerId)}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/customers` ที่ contract รับ — และไม่มีตัวที่สี่
 *
 * ไม่มีพารามิเตอร์ค้นหาเลยโดยตั้งใจของ contract ช่องค้นหาบนหน้าจอจึงกรอง
 * **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ใช่ยิงคำขอใหม่ (ดู `customersView.ts`)
 * ส่วน `status` เป็นตัวกรองของ **เซิร์ฟเวอร์** จริง ๆ จึงเดินทางไปใน query
 */
export interface CustomerListQuery {
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่
 * "หน้าแรก" สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 */
export function buildCustomerListQuery(query: CustomerListQuery = {}): string {
  const params = new URLSearchParams();
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก
   * `CUSTOMER_CODE_IS_IMMUTABLE` ออกจาก `CUSTOMER_FIELD_IS_SERVER_OWNED` และจาก
   * `400` ธรรมดาของ payload ที่ผิดรูป — ทั้งสามมาด้วย `400` เท่ากัน
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้แปลว่า **แผนกของคุณไม่ได้ถือโมดูล `CUSTOMER_MASTER`** ไม่ใช่
 * "โรงงานนอกขอบเขต": ตาราง customer เป็นข้อมูลระดับองค์กรและไม่มี `plant_id` เลย
 * (D-089) จึงไม่มี plant scope ให้ชนตั้งแต่ต้น — และเพราะมันเป็นโมดูลธุรกิจปกติ
 * `ADMIN` ก็ถูกปฏิเสธด้วย `403` เช่นกัน (D-060)
 *
 * `404` แปลว่า **ลูกค้ารายที่คำขออ้างถึงไม่มีอยู่แล้ว** ซึ่งเกิดได้จริงจากรายการที่
 * เปิดค้างไว้ขณะที่อีกคนแก้ข้อมูล — ไม่ใช่ข้อผิดพลาดของระบบ
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: รหัสลูกค้า
 * ที่ซ้ำเป็นสิ่งที่ผู้ใช้แก้เองได้ในสองวินาที ส่วน "เซิร์ฟเวอร์ทำงานไม่สำเร็จ" คือ
 * สิ่งที่เขาทำอะไรไม่ได้เลย
 *
 * `rule-violation` (`422`) อยู่ในชุดนี้เพื่อความครบถ้วนของ envelope ฝั่งเซิร์ฟเวอร์
 * — ทะเบียนลูกค้ายังไม่มีกฎข้อใดที่ตอบด้วยรหัสนั้น (กฎ `CUSTOMER_IS_BLOCKED` และ
 * `CUSTOMER_IS_INACTIVE` เป็นของ Sales Order ที่ P2-4b ไม่ใช่ของทะเบียนนี้) การกลืน
 * มันเป็น `server-error` จะทำให้กฎข้อแรกที่ถูกเพิ่มในอนาคตอ่านเหมือนระบบพัง
 */
export type CustomerFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type CustomerListResult =
  | { readonly status: 'ready'; readonly page: CustomerListPage }
  | CustomerFailure;

export type CustomerResult =
  | { readonly status: 'ready'; readonly customer: Customer }
  | CustomerFailure;

/**
 * ผลของการอ่าน **ทะเบียนทั้งชุด** — `items` คือทุกหน้าที่ต่อกันแล้ว ไม่มี cursor ให้เดินต่อ
 *
 * ไม่มี `next_cursor` ในรูปนี้โดยตั้งใจ: cursor ที่ยังเหลืออยู่แปลว่ายังอ่านไม่ครบ
 * ซึ่งเป็นสิ่งที่ `fetchAllCustomers` สัญญาว่าจะไม่คืน
 */
export type CustomerListAllResult =
  | { readonly status: 'ready'; readonly items: readonly Customer[] }
  | CustomerFailure;

export interface CustomerRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
 * จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * ที่อยู่ของลูกค้า (D-121) — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีที่อยู่"
 *
 * คีย์ที่ **หายไปทั้งคีย์** ถือเป็น `null` ไม่ใช่ความผิดรูป: คำตอบจาก API รุ่นก่อน
 * D-121 ไม่มีคีย์นี้ และการปฏิเสธคำตอบนั้นจะทำให้รายการลูกค้าทั้งหน้าหายไป ทั้งที่
 * สิ่งที่ขาดเป็นข้อมูลเสริม ไม่ใช่ข้อมูลที่ตารางต้องใช้
 *
 * ที่อยู่ที่เป็นช่องว่างล้วนกลายเป็น `null` — คอลัมน์ฝั่งเซิร์ฟเวอร์บังคับ
 * `min_length=1` อยู่แล้ว และ "ช่องว่างสามตัว" ไม่ใช่ที่อยู่ที่ส่งของไปถึง
 */
function parseAddress(raw: unknown): string | null | undefined {
  const address = nullableString(raw);
  if (address === undefined) {
    return undefined;
  }
  return address === null || address.trim() === '' ? null : address;
}

/**
 * ผู้ติดต่อหนึ่งราย — `null` แปลว่าอ่านไม่ออก
 *
 * `contact_no` ต้องเป็นจำนวนเต็มจริง ไม่ใช่ `"2"` หรือ `2.5`: มันเป็นครึ่งหนึ่งของ
 * primary key ฝั่งเซิร์ฟเวอร์ และค่าที่หน้าจอ coerce เองจะทำให้สองช่องที่ต่างกัน
 * กลายเป็นช่องเดียวกันเงียบ ๆ
 */
function parseCustomerContact(raw: unknown): CustomerContact | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const contactNo = record.contact_no;
  const contactName = requiredString(record.contact_name);
  const email = nullableString(record.email);
  const phone = nullableString(record.phone);

  if (
    typeof contactNo !== 'number' ||
    !Number.isInteger(contactNo) ||
    contactName === null ||
    email === undefined ||
    phone === undefined
  ) {
    return null;
  }

  return { contact_no: contactNo, contact_name: contactName, email, phone };
}

/**
 * รายชื่อผู้ติดต่อ **เรียงตาม `contact_no`** — `undefined` แปลว่าผิดรูป
 *
 * คีย์ที่หายไปทั้งคีย์ (และ `null`) ถือเป็นรายการว่าง ด้วยเหตุผลเดียวกับ
 * `parseAddress`: API รุ่นก่อน D-121 ไม่มีคีย์นี้
 *
 * เรียงที่ parser **ไม่ใช่ที่หน้าจอ** เพื่อให้ผู้อ่านทุกตัวได้ลำดับเดียวกัน — ลำดับ
 * ที่แสดงต้องเป็นลำดับของช่อง (`ผู้ติดต่อ 1, 2, 3`) ไม่ใช่ลำดับที่เซิร์ฟเวอร์เผอิญ
 * ส่งแถวมา และแถวเดียวที่อ่านไม่ออกทำให้ **ทั้งรายนั้น** ผิดรูป ไม่ใช่ถูกตัดทิ้งเงียบ ๆ
 */
function parseCustomerContacts(raw: unknown): readonly CustomerContact[] | undefined {
  if (raw === null || raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    return undefined;
  }

  const contacts: CustomerContact[] = [];
  for (const entry of raw) {
    const parsed = parseCustomerContact(entry);
    if (parsed === null) {
      return undefined;
    }
    contacts.push(parsed);
  }
  return contacts.sort((left, right) => left.contact_no - right.contact_no);
}

/**
 * หนึ่งลูกค้า — **บังคับครบทั้งหกฟิลด์ที่ contract บังคับ**
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของสามค่าที่หน้าจอรู้จัก:
 * CHECK constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่สี่ทิ้ง
 * ตรงนี้จะทำให้ **ลูกค้าที่มีอยู่จริงหายไปจากรายการ** ในวันที่ชุดนั้นถูกขยาย ซึ่ง
 * แย่กว่าการแสดงรหัสที่หน้าจอยังไม่มีคำอธิบายให้ (ดู `customerStatusLabel`)
 */
export function parseCustomer(raw: unknown): Customer | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const customerId = requiredString(record.customer_id);
  const customerCode = requiredString(record.customer_code);
  const customerName = requiredString(record.customer_name);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  // D-121 — **ไม่บังคับ** คำตอบที่ไม่มีสองคีย์นี้ยังอ่านได้ (ดู `parseAddress`)
  const address = parseAddress(record.address);
  const contacts = parseCustomerContacts(record.contacts);

  if (
    customerId === null ||
    customerCode === null ||
    customerName === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null ||
    address === undefined ||
    contacts === undefined
  ) {
    return null;
  }

  return {
    customer_id: customerId,
    customer_code: customerCode,
    customer_name: customerName,
    status,
    address,
    contacts,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการลูกค้าจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย — parser ที่เขียน
 * ขึ้นจากรูปที่เดาเอาเองคือ defect ที่ทำให้หน้าจอ BOM เคยขึ้นว่า "ยังไม่มีสูตร"
 * ให้สินค้าที่มีสูตรอยู่แล้ว
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ **ทั้งหน้าผิดรูป** ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: รายการที่
 * ขาดไปหนึ่งแถวคือหน้าจอที่บอกผู้ใช้ว่ารหัสนั้นยังว่างอยู่ แล้วเขาจะกดสร้างซ้ำ
 */
export function parseCustomerListPage(raw: unknown): CustomerListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: Customer[] = [];
  for (const entry of record.items) {
    const parsed = parseCustomer(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/customers` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม: `extra = "forbid"` ทำให้
 * คีย์แปลกปลอมที่ติดมา (เช่นแถวของลูกค้าที่ถูก spread ทั้งก้อนพร้อม `customer_id`)
 * กลายเป็น `400 CUSTOMER_FIELD_IS_SERVER_OWNED` ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และ
 * เป็นด่านที่ทำให้ `customer_id` ไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** ต่อให้ผู้เรียกถือ
 * object ที่มีคีย์นั้นอยู่ในมือ
 *
 * `status` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง: contract
 * บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้
 *
 * `address`/`contacts` (D-126) หายไปทั้งคีย์เมื่อไม่ได้ระบุเช่นกัน — ไม่มี `null` ที่นี่
 * เพราะ `POST` ไม่มีค่าเดิมให้ "ล้าง" แบบ `PATCH`
 */
export function serializeCreateCustomerBody(
  body: CreateCustomerBody,
): Record<string, unknown> {
  return {
    customer_code: body.customer_code,
    customer_name: body.customer_name,
    ...(body.status === undefined ? {} : { status: body.status }),
    ...(body.address === undefined ? {} : { address: body.address }),
    ...(body.contacts === undefined
      ? {}
      : {
          contacts: body.contacts.map((contact) => ({
            contact_no: contact.contact_no,
            contact_name: contact.contact_name,
            email: contact.email,
            phone: contact.phone,
          })),
        }),
  };
}

/**
 * แปลง body ของ `PATCH /v1/customers/{customer_id}` — **`customer_code` ออกไปไม่ได้**
 *
 * ด่านสุดท้ายของกฎที่ `UpdateCustomerBody` ประกาศไว้ด้วยชนิดข้อมูล: คีย์ที่ไม่ได้
 * ระบุจะไม่เดินทางไปเลย ("ปล่อยไว้ตามเดิม") และรหัสลูกค้าไม่มีทางหลุดไปกับคำขอ
 * ซึ่งจะถูกตอบ `400 CUSTOMER_CODE_IS_IMMUTABLE` ทั้งที่ผู้ใช้แค่แก้ชื่อ
 *
 * `address` ที่เป็น `null` ยังถูกส่งไป (ไม่ใช่ถูกตัดคีย์ทิ้ง) — มันคือคำสั่ง "ล้าง
 * ที่อยู่" (D-125) ตรงข้ามกับ `undefined` ซึ่งแปลว่า "ไม่แตะ" `contacts` ก็เช่นกัน:
 * เมื่อผู้เรียกส่งมา มันถูกส่งทั้งรายการเสมอ รวมถึงรายการว่าง `[]`
 */
export function serializeUpdateCustomerBody(
  body: UpdateCustomerBody,
): Record<string, unknown> {
  return {
    ...(body.customer_name === undefined ? {} : { customer_name: body.customer_name }),
    ...(body.status === undefined ? {} : { status: body.status }),
    ...(body.address === undefined ? {} : { address: body.address }),
    ...(body.contacts === undefined
      ? {}
      : {
          contacts: body.contacts.map((contact) => ({
            contact_no: contact.contact_no,
            contact_name: contact.contact_name,
            email: contact.email,
            phone: contact.phone,
          })),
        }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | CustomerFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: CustomerRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** อ่านคำตอบที่ต้องเป็นลูกค้าหนึ่งราย — payload ที่ผิดรูปไม่ใช่ "สำเร็จ" */
function toCustomerResult(result: PayloadResult): CustomerResult {
  if (result.status !== 'payload') {
    return result;
  }
  const customer = parseCustomer(result.payload);
  return customer === null ? { status: 'invalid-response' } : { status: 'ready', customer };
}

/**
 * `GET /v1/customers` — ทะเบียนลูกค้าทั้งองค์กร เรียงตาม `customer_code`
 *
 * **ไม่เรียงลำดับซ้ำเอง** contract ระบุว่าเซิร์ฟเวอร์เป็นผู้เรียง การเรียงซ้ำที่
 * frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจนไม่มีใครเห็น
 */
export async function fetchCustomers(
  query: CustomerListQuery = {},
  options: CustomerRequestOptions = {},
): Promise<CustomerListResult> {
  const result = await requestPayload(
    `${CUSTOMERS_PATH}${buildCustomerListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseCustomerListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * จำนวนแถวที่ขอต่อหนึ่งคำขอตอนอ่านทะเบียนทั้งชุด
 *
 * ใหญ่พอให้ทะเบียนขนาดจริงจบในคำขอเดียวหรือสองคำขอ และยังเป็นค่าที่ contract รับ
 * — ไม่ใช่ "ขอให้มากที่สุดเท่าที่เซิร์ฟเวอร์ยอม" ซึ่งเป็นตัวเลขที่ไม่มีใครรู้
 */
export const CUSTOMER_LIST_ALL_PAGE_SIZE = 200;

/**
 * อ่านทะเบียนลูกค้า **ทุกหน้า** แล้วต่อเป็นรายการเดียว (D-123 ข้อ 1)
 *
 * หน้าจอรายการเรียง/แบ่งหน้าฝั่งเบราว์เซอร์ จึงต้องถือทั้งชุดอยู่ในมือก่อน: การ
 * เรียงเฉพาะหน้าที่โหลดมาแล้วไม่ใช่การเรียง มันคือการสลับลำดับของกลุ่มตัวอย่างที่
 * เซิร์ฟเวอร์เลือกมาให้ และผู้ใช้ที่กด "ชื่อลูกค้า" จะเชื่อว่าเขาเห็นรายแรกตามตัวอักษร
 * ทั้งที่รายนั้นอยู่หน้าถัดไป
 *
 * **หน้าไหนล้มเหลว = ผลทั้งก้อนล้มเหลว** ไม่ใช่คืนเท่าที่อ่านได้: รายการครึ่งเดียวที่
 * ดูเหมือนครบคือหน้าจอที่บอกว่าลูกค้ารายที่หายไปไม่มีอยู่ ซึ่งจะพาไปสู่การสร้างรหัส
 * ที่มีเจ้าของอยู่แล้วซ้ำเข้าไป (เหตุผลเดียวกับที่ `parseCustomerListPage` ไม่ตัดแถว
 * ที่อ่านไม่ออกทิ้ง)
 *
 * `signal` เดิมถูกส่งต่อให้ทุกคำขอ — การยกเลิกกลางทางจึงหยุดทั้งลูป และคืน
 * `cancelled` ไม่ใช่รายการที่อ่านได้บางส่วน
 *
 * cursor ที่ซ้ำกับที่เคยเดินผ่านแล้วถือเป็น `invalid-response` **ไม่ใช่สัญญาณให้วนต่อ**:
 * เซิร์ฟเวอร์ที่ส่ง cursor เดิมกลับมาคือลูปที่ไม่มีวันจบ และหน้าจอที่ค้างอยู่กับ
 * สปินเนอร์ตลอดไปอธิบายตัวเองไม่ได้เลย
 */
export async function fetchAllCustomers(
  query: CustomerListQuery = {},
  options: CustomerRequestOptions = {},
): Promise<CustomerListAllResult> {
  const items: Customer[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = query.cursor?.trim() ?? null;

  for (;;) {
    const result = await fetchCustomers(
      {
        ...query,
        limit: CUSTOMER_LIST_ALL_PAGE_SIZE,
        ...(cursor === null || cursor === '' ? {} : { cursor }),
      },
      options,
    );
    if (result.status !== 'ready') {
      return result;
    }

    items.push(...result.page.items);
    cursor = result.page.next_cursor;
    if (cursor === null) {
      return { status: 'ready', items };
    }
    if (seenCursors.has(cursor)) {
      return { status: 'invalid-response' };
    }
    seenCursors.add(cursor);
  }
}

/**
 * `GET /v1/customers/{customer_id}` — ลูกค้ารายเดียว คืนรูปเดียวกับ `POST`
 *
 * `404` ของเส้นนี้ **ไม่ใช่ความล้มเหลวของระบบ**: มันคือคำตอบว่าลูกค้ารายนั้นไม่มี
 * อยู่ (หรือไม่เคยมี) ซึ่งเป็นข้อเท็จจริงที่ผู้เรียกต้องแยกออกจาก "อ่านไม่สำเร็จ"
 */
export async function fetchCustomer(
  customerId: string,
  options: CustomerRequestOptions = {},
): Promise<CustomerResult> {
  return toCustomerResult(
    await requestPayload(
      customerPath(customerId),
      { method: 'GET', headers: { Accept: 'application/json' } },
      options,
    ),
  );
}

/**
 * `POST /v1/customers` — สร้างลูกค้าใหม่ ตอบ `201`
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การสร้างทะเบียนลูกค้าไม่
 * เคลื่อนสต๊อกหรือบัญชี และรหัสที่ซ้ำถูกกันด้วย unique constraint อยู่แล้ว
 * (`409 CUSTOMER_CODE_TAKEN` โดยไม่เขียนอะไรเลย) การเติมคีย์ที่ endpoint ไม่ได้ขอ
 * เข้าไปคือ header ที่ไม่มีใครอ่าน ซึ่งจะสอนคนอ่านโค้ดผิดว่า endpoint นี้ต้องการมัน
 */
export async function createCustomer(
  body: CreateCustomerBody,
  options: CustomerRequestOptions = {},
): Promise<CustomerResult> {
  return toCustomerResult(
    await requestPayload(
      CUSTOMERS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateCustomerBody(body)),
      },
      options,
    ),
  );
}

/**
 * `PATCH /v1/customers/{customer_id}` — แก้ชื่อ และ/หรือ สถานะ
 *
 * เซิร์ฟเวอร์เขียน `audit_log` action `UPDATE` (before/after) **เสมอแม้ไม่มีอะไร
 * เปลี่ยน** ในธุรกรรมเดียวกับการแก้ข้อมูล — ผู้เรียกจึงอ่านสถานะใหม่จาก **คำตอบ**
 * ไม่ใช่จากสิ่งที่ตัวเองเพิ่งส่งไป
 */
export async function updateCustomer(
  customerId: string,
  body: UpdateCustomerBody,
  options: CustomerRequestOptions = {},
): Promise<CustomerResult> {
  return toCustomerResult(
    await requestPayload(
      customerPath(customerId),
      {
        method: 'PATCH',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeUpdateCustomerBody(body)),
      },
      options,
    ),
  );
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: CustomerFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
